/**
 * Signed-in gate for the legal texts: when the Terms / Privacy notice changed since the player accepted them (or the
 * account predates the legal pages), the game waits for a new, explicit acceptance. If the state cannot be read the
 * game opens anyway (a transient error must not lock people out); the server asks again on the next load.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { LEGAL_PATHS } from '@galaxis/shared';
import type { LegalConsentState } from '@galaxis/shared';
import { fetchLegalState, acceptLegal, logout } from '../api/account.ts';
import { APP_NAME } from '../brand.ts';

const message = (err: unknown, fallback: string) => (err instanceof Error && err.message ? err.message : fallback);

export function LegalGate({ children }: { children: ReactNode }) {
  const [state, setState] = useState<LegalConsentState | 'loading' | 'error'>('loading');
  useEffect(() => {
    let live = true;
    fetchLegalState().then(s => { if (live) setState(s); }).catch(() => { if (live) setState('error'); });
    return () => { live = false; };
  }, []);

  if (state === 'loading') return <div className="w-full h-full flex items-center justify-center bg-space-900 text-slate-400 text-sm" role="status">Betöltés…</div>;
  if (state === 'error' || !state.needsAcceptance) return <>{children}</>;
  return <Reaccept state={state} onDone={s => setState(s)} />;
}

function Reaccept({ state, onDone }: { state: LegalConsentState; onDone: (s: LegalConsentState) => void }) {
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const first = !state.accepted;

  const accept = async () => {
    setBusy(true); setError(null);
    try { onDone(await acceptLegal(state.current)); } catch (err) { setError(message(err, 'Az elfogadás nem sikerült.')); setBusy(false); }
  };
  const signOut = async () => { try { await logout(); } finally { window.location.replace('/'); } };

  return (
    <div className="w-full h-full overflow-y-auto bg-space-900 flex items-center justify-center px-4 py-8" data-testid="legal-gate">
      <div className="w-full max-w-md sci-fi-panel rounded-xl p-5 flex flex-col gap-4" role="dialog" aria-modal="true" aria-labelledby="legal-gate-title">
        <h1 id="legal-gate-title" className="font-mono text-xl font-bold tracking-widest text-accent uppercase">{APP_NAME}</h1>
        <h2 className="text-base font-semibold text-slate-100">{first ? 'Felhasználási feltételek és adatkezelés' : 'Frissültek a feltételek'}</h2>
        <p className="text-sm text-slate-300">
          {first
            ? 'A játék folytatásához kérjük, olvasd el és fogadd el a feltételeket.'
            : 'A Felhasználási feltételek vagy az Adatkezelési tájékoztató megváltozott. A játék folytatásához kérjük, olvasd el az új változatot, és fogadd el.'}
        </p>
        <ul className="text-sm text-slate-300 list-disc pl-5">
          <li><a href={LEGAL_PATHS.terms} target="_blank" rel="noopener" className="text-accent underline">Felhasználási feltételek</a> ({state.current.terms})</li>
          <li><a href={LEGAL_PATHS.privacy} target="_blank" rel="noopener" className="text-accent underline">Adatkezelési tájékoztató</a> ({state.current.privacy})</li>
        </ul>
        <label className="flex items-start gap-3 text-sm text-slate-300 cursor-pointer" style={{ minHeight: 44 }}>
          <input type="checkbox" className="mt-1" style={{ width: 20, height: 20 }} checked={checked} onChange={e => setChecked(e.target.checked)} data-testid="legal-gate-check" />
          <span>Elolvastam és elfogadom a fenti dokumentumok jelenlegi változatát.</span>
        </label>
        {error && <div className="notice notice-error" role="alert">{error}</div>}
        <button type="button" className="btn-primary" disabled={!checked || busy} onClick={() => void accept()} data-testid="legal-gate-accept">{busy ? 'Mentés…' : 'Elfogadom, folytatom'}</button>
        <p className="text-xs text-slate-500">Ha nem fogadod el, kijelentkezhetsz. Adataid letöltését vagy a fiókod törlését az info@imronit.hu címen is kérheted.</p>
        <button type="button" className="btn-secondary" onClick={() => void signOut()} data-testid="legal-gate-logout">Kijelentkezés</button>
      </div>
    </div>
  );
}
